import { useEffect, useState } from "react";
import { WifiOff, Wifi, RefreshCw } from "lucide-react";
import { syncQueue, queueCount } from "@/lib/offlineQueue";
import { toast } from "sonner";

export function ConnectionBadge() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = async () => {
    const n = await queueCount();
    setPending(n);
    return n;
  };

  useEffect(() => {
    refreshPending();
    const handleOnline = async () => {
      setOnline(true);
      setShowBackOnline(true);
      const n = await refreshPending();
      if (n > 0) {
        setSyncing(true);
        const { synced, failed } = await syncQueue();
        setSyncing(false);
        await refreshPending();
        if (synced > 0) toast.success(`Sincronizzati ${synced} elementi offline`);
        if (failed > 0) toast.error(`${failed} elementi non sincronizzati (verranno riprovati)`);
      }
      setTimeout(() => setShowBackOnline(false), 3000);
    };
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    // Refresh pending count periodically
    const interval = setInterval(refreshPending, 5000);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  const showOfflinePending = !online || pending > 0 || showBackOnline || syncing;
  if (!showOfflinePending) return null;

  let bg = "bg-emerald-600";
  let content;
  if (syncing) {
    bg = "bg-brand";
    content = <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Sincronizzo {pending} elementi offline...</>;
  } else if (!online) {
    bg = "bg-amber-500";
    content = <>
      <WifiOff className="h-3.5 w-3.5" />
      Modalità offline — {pending > 0 ? `${pending} elementi in coda. ` : ""}Sarà tutto sincronizzato al ritorno online.
    </>;
  } else if (pending > 0) {
    bg = "bg-brand";
    content = <><RefreshCw className="h-3.5 w-3.5" /> {pending} elementi in coda da sincronizzare</>;
  } else if (showBackOnline) {
    content = <><Wifi className="h-3.5 w-3.5" /> Connessione ripristinata</>;
  }

  return (
    <div
      data-testid="connection-badge"
      role="status"
      className={`fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2 py-2 px-4 text-xs font-medium shadow-md text-white ${bg}`}
    >
      {content}
    </div>
  );
}
